import { defineStore, storeToRefs } from 'pinia';
import CustomStore from 'devextreme/data/custom_store';
import type { LoadOptions } from 'devextreme/data';
import type {
  AccountListStoreOptions,
  AccountListState,
  AccountListModel,
  AccountModel,
  PendingAccountListModel,
  PendingAccountListStoreOptions
} from '~/types';
import useAccountsApi from '~/composables/api/useAccountsApi';
import { normalizeFilters, updateFilter } from '@/utils/account-list-filters';
import useAccountsSearch from '~/composables/accounts/useAccountsSearch';
import { useStore } from '~/stores/store';

export const useAccountListStore = defineStore('account-list', {
  state: (): AccountListState => ({
    totalCount: undefined,
    size: 20,
    sort: 'desc',
    filter: undefined,
    actualSkipToken: undefined,
    actualSkipDirection: undefined,
    nextLink: undefined,
    prevLink: undefined,
    isDataLoaded: false,
    accountOptions: {},
    needsDataReloading: false,
    idFilter: '',
    statusFilter: '',
    typeFilter: '',
    nameFilter: '',
    emailFilter: '',
    activityTypeFilter: '',
    profileStatusTypeFilter: '',
    profileTypeFilter: '',
    requirementTypeFilter: '',
    requirementStatusFilter: '',
    restrictionReasonFilter: '',
    searchTerm: undefined
  }),

  actions: {
    extractSkipToken(link: string | undefined): string | undefined {
      const skipTokenMatch = link?.match(/\$skipToken=([^&]+)/);
      return skipTokenMatch ? skipTokenMatch[1] : undefined;
    },

    getSkipToken(
      storeOptions: AccountListStoreOptions,
      _loadOptions: LoadOptions,
      filters: string | undefined
    ): string | undefined {
      if (this.filter !== filters) {
        return undefined;
      }

      if (storeOptions.pageDirection === 'next') {
        return this.extractSkipToken(this.nextLink);
      }

      if (storeOptions.pageDirection === 'prev') {
        return this.extractSkipToken(this.prevLink);
      }

      return undefined;
    },

    createParams(options: AccountListStoreOptions | PendingAccountListStoreOptions): string {
      return Object.entries(options)
        .filter((value) => value[1] !== undefined)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&');
    },

    setIdFilter(value: string) {
      this.idFilter = value;
    },
    setStatusFilter(value: string) {
      this.statusFilter = value;
    },
    setTypeFilter(value: string) {
      this.typeFilter = value;
    },
    setNameFilter(value: string) {
      this.nameFilter = value;
    },
    setEmailFilter(value: string) {
      this.emailFilter = value;
    },
    setActivityTypeFilter(value: string) {
      this.activityTypeFilter = value;
    },
    setProfileStatusTypeFilter(value: string) {
      this.profileStatusTypeFilter = value;
    },
    setProfileTypeFilter(value: string) {
      this.profileTypeFilter = value;
    },
    setRequirementTypeFilter(value: string) {
      this.requirementTypeFilter = value;
    },
    setRequirementStatusFilter(value: string) {
      this.requirementStatusFilter = value;
    },
    setRestrictionReasonFilter(value: string) {
      this.restrictionReasonFilter = value;
    },
    setSearchTerm(value?: string) {
      this.searchTerm = value;
    },
    resetExternalFilters() {
      this.idFilter = '';
      this.statusFilter = '';
      this.typeFilter = '';
      this.nameFilter = '';
      this.emailFilter = '';
      this.activityTypeFilter = '';
      this.profileStatusTypeFilter = '';
      this.profileTypeFilter = '';
      this.requirementTypeFilter = '';
      this.requirementStatusFilter = '';
      this.restrictionReasonFilter = '';
      this.searchTerm = undefined;
    },

    computeAccountListOptions(
      _loadOptions: LoadOptions,
      storeOptions: AccountListStoreOptions,
      exportSkipToken?: string
    ): AccountListStoreOptions {
      const { dateRange } = storeToRefs(useStore());
      const { computeSearchParams } = useAccountsSearch();

      const isForExport = exportSkipToken !== undefined || Boolean(storeOptions.isForExport);

      const firstSort = Array.isArray(_loadOptions.sort) ? _loadOptions.sort[0] : _loadOptions.sort;

      const sort: 'asc' | 'desc' =
        (firstSort as { desc?: boolean } | undefined)?.desc === false ? 'asc' : 'desc';

      const normalized = normalizeFilters(_loadOptions as LoadOptions, dateRange.value);
      let { filtersArr } = normalized;
      const { identityNameValue } = normalized;

      filtersArr = updateFilter(
        filtersArr,
        'id',
        (storeOptions.idFilter ?? this.idFilter) as string
      );
      filtersArr = updateFilter(
        filtersArr,
        'status',
        (storeOptions.statusFilter ?? this.statusFilter) as string,
        true
      );
      filtersArr = updateFilter(
        filtersArr,
        'type',
        (storeOptions.typeFilter ?? this.typeFilter) as string,
        true
      );
      filtersArr = updateFilter(
        filtersArr,
        'identity.email',
        (storeOptions.emailFilter ?? this.emailFilter) as string
      );
      filtersArr = updateFilter(
        filtersArr,
        'profiles.activityType',
        (storeOptions.activityTypeFilter ?? this.activityTypeFilter) as string,
        true
      );
      filtersArr = updateFilter(
        filtersArr,
        'profiles.status.type',
        (storeOptions.profileStatusTypeFilter ?? this.profileStatusTypeFilter) as string,
        true
      );
      filtersArr = updateFilter(
        filtersArr,
        'profiles.type',
        (storeOptions.profileTypeFilter ?? this.profileTypeFilter) as string,
        true
      );
      filtersArr = updateFilter(
        filtersArr,
        'requirements.type',
        (storeOptions.requirementTypeFilter ?? this.requirementTypeFilter) as string,
        true
      );
      filtersArr = updateFilter(
        filtersArr,
        'requirements.status',
        (storeOptions.requirementStatusFilter ?? this.requirementStatusFilter) as string,
        true
      );
      filtersArr = updateFilter(
        filtersArr,
        'restrictions.reason',
        (storeOptions.restrictionReasonFilter ?? this.restrictionReasonFilter) as string,
        true
      );

      const filters = filtersArr.join(',');

      const nameCandidate =
        (storeOptions.nameFilter ?? this.nameFilter)?.trim() || identityNameValue;
      const { searchTerm, searchFields } = computeSearchParams(nameCandidate, {
        ...storeOptions,
        searchTerm: storeOptions.searchTerm ?? this.searchTerm
      });

      if (isForExport) {
        return {
          orderBy: 'createdAt',
          size: storeOptions.size ?? this.size,
          sort,
          filter: filters || undefined,
          skipToken: exportSkipToken,
          skipDirection: exportSkipToken ? 'next' : undefined,
          searchTerm,
          searchFields
        };
      }

      const skipToken = this.getSkipToken(storeOptions, _loadOptions, filters);

      return {
        orderBy: 'createdAt',
        size: storeOptions.size ?? this.size,
        sort,
        filter: filters || undefined,
        skipToken: sort === this.sort ? skipToken : undefined,
        skipDirection: (skipToken && storeOptions.pageDirection) ?? undefined,
        searchTerm,
        searchFields
      };
    },

    setDataStore(storeOptions: AccountListStoreOptions) {
      const { getAccountList } = useAccountsApi();

      return new CustomStore({
        key: 'Id',
        loadMode: 'processed',
        load: async (_loadOptions: LoadOptions) => {
          const options = this.computeAccountListOptions(_loadOptions, storeOptions);

          this.filter = options.filter;
          this.size = options.size ?? 20;
          this.sort = options.sort ?? 'desc';

          const isLoadingAll = (_loadOptions as { isLoadingAll?: boolean }).isLoadingAll;

          if (isLoadingAll) {
            options.skipToken = this.actualSkipToken;
            options.skipDirection = this.actualSkipDirection;
          } else {
            this.actualSkipToken = options.skipToken;
            this.actualSkipDirection = options.skipDirection;
          }

          const data: AccountListModel = await getAccountList(options);

          this.nextLink = data.NextLink;
          this.prevLink = data.PrevLink;
          this.totalCount = data.Data.length;

          return {
            data: data.Data,
            totalCount: this.totalCount
          };
        }
      });
    },

    async loadAllAccountsForExport(
      _loadOptions: LoadOptions,
      maxRows: number
    ): Promise<AccountModel[]> {
      const { getAccountList } = useAccountsApi();
      const accounts: AccountModel[] = [];
      let skipToken: string | undefined;

      do {
        const options = this.computeAccountListOptions(
          _loadOptions,
          { isForExport: true },
          skipToken
        );
        const data: AccountListModel = await getAccountList(options);
        accounts.push(...data.Data);
        skipToken = this.extractSkipToken(data.NextLink);
      } while (skipToken && accounts.length < maxRows);

      return accounts;
    },
    setPendingAccountsDataStore(storeOptions: AccountListStoreOptions) {
      const { getPendingAccountsList } = useAccountsApi();
      const { dateRange } = storeToRefs(useStore());
      const { computeSearchParams } = useAccountsSearch();

      return new CustomStore({
        key: 'Id',
        loadMode: 'processed',
        load: async (_loadOptions: any) => {
          const sort = _loadOptions.sort && _loadOptions.sort?.[0]?.desc === false ? 'asc' : 'desc';
          const normalized = normalizeFilters(_loadOptions as LoadOptions, dateRange.value);
          let { filtersArr } = normalized;
          const { identityNameValue } = normalized;

          filtersArr = updateFilter(
            filtersArr,
            'id',
            (storeOptions.idFilter ?? this.idFilter) as string
          );

          filtersArr = updateFilter(
            filtersArr,
            'type',
            (storeOptions.typeFilter ?? this.typeFilter) as string,
            true
          );
          filtersArr = updateFilter(
            filtersArr,
            'identity.email',
            (storeOptions.emailFilter ?? this.emailFilter) as string
          );

          const filters = filtersArr.join(',');

          const nameCandidate =
            (storeOptions.nameFilter ?? this.nameFilter)?.trim() || identityNameValue;
          const { searchTerm, searchFields } = computeSearchParams(nameCandidate, {
            ...storeOptions,
            searchTerm: storeOptions.searchTerm ?? this.searchTerm
          });

          const skipToken = this.getSkipToken(storeOptions, _loadOptions, filters);

          const options: PendingAccountListStoreOptions = {
            orderBy: 'createdAt',
            size: storeOptions.size ?? this.size,
            sort,
            filter: filters || undefined,
            skipToken: sort === this.sort ? skipToken : undefined,
            skipDirection: (skipToken && storeOptions.pageDirection) ?? undefined,
            searchTerm,
            searchFields
          };

          this.filter = options.filter;
          this.size = options.size ?? 20;
          this.sort = options.sort ?? 'desc';

          this.actualSkipToken = options.skipToken;
          this.actualSkipDirection = options.skipDirection;

          const { Data, NextLink, PrevLink }: PendingAccountListModel =
            await getPendingAccountsList(options);

          this.nextLink = NextLink;
          this.prevLink = PrevLink;
          this.totalCount = Data.length;

          return {
            data: Data,
            totalCount: this.totalCount
          };
        }
      });
    },
    reloadData() {
      this.totalCount = undefined;
      this.size = 20;
      this.sort = 'desc';
      this.filter = undefined;
      this.actualSkipToken = undefined;
      this.actualSkipDirection = undefined;
      this.nextLink = undefined;
      this.prevLink = undefined;
      this.isDataLoaded = false;
      this.needsDataReloading = false;
    }
  }
});
